import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme
} from '@mui/material';
import {
  Link as LinkIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  PlayArrow as TestIcon
} from '@mui/icons-material';
import { Agent, CRMConfiguration } from '../../types/agent';
import CRMConfigurationDialog from '../admin/CRMConfigurationDialog';
import { agentApi } from '../../utils/agentApi';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface CRMIntegrationCardProps {
  agent: Agent;
  onAgentUpdate?: (agent: Agent) => void;
}

const CRMIntegrationCard: React.FC<CRMIntegrationCardProps> = ({ agent, onAgentUpdate }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResultDialogOpen, setTestResultDialogOpen] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    testData?: any;
    error?: string;
  } | null>(null);
  const [crmConfig, setCrmConfig] = useState<CRMConfiguration>(
    agent.agentConfig?.crmConfiguration || {
      enabled: false,
      url: '',
      method: 'POST',
      headers: [{ key: 'Content-Type', value: 'application/json' }],
      fieldMappings: [],
      customBody: ''
    }
  );

  const saveCRMConfig = async (config: CRMConfiguration) => {
    setIsSaving(true);
    try {
      const updatedAgent = {
        ...agent,
        agentConfig: {
          ...agent.agentConfig,
          crmConfiguration: config,
          // Manter compatibilidade com campos legados
          enviarLeedCRM: config.enabled,
          urlLeedCRM: config.url,
          TokenLeedCRM: config.headers.find(h => h.key.toLowerCase().includes('token') || h.key.toLowerCase().includes('authorization'))?.value || ''
        }
      };

      await agentApi.updateAgent(agent.id, updatedAgent);
      
      if (onAgentUpdate) {
        onAgentUpdate(updatedAgent);
      }
      
      toast.success(t('agents.crm.saveSuccess'));
    } catch (error) {
      console.error('Erro ao salvar configuração CRM:', error);
      toast.error(t('agents.crm.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleCRM = async (enabled: boolean) => {
    const updatedConfig = {
      ...crmConfig,
      enabled
    };
    
    setCrmConfig(updatedConfig);
    await saveCRMConfig(updatedConfig);
  };

  const handleConfigSave = async (newConfig: CRMConfiguration) => {
    setCrmConfig(newConfig);
    setConfigDialogOpen(false);
    await saveCRMConfig(newConfig);
  };

  const handleTestCRM = async () => {
    if (!crmConfig.enabled || !crmConfig.url) {
      toast.error(t('agents.crm.configureBeforeTest'));
      return;
    }

    setIsTesting(true);
    try {
      const result = await agentApi.testCRMConfiguration(agent.id);
      setTestResult({
        success: result.success,
        message: result.message,
        testData: result.testData,
        error: result.error
      });
      setTestResultDialogOpen(true);
      
      if (result.success) {
        toast.success(t('agents.crm.testSuccess'));
      } else {
        toast.error(t('agents.crm.testError'));
      }
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.message || t('agents.crm.testErrorGeneric'),
        error: error.message
      });
      setTestResultDialogOpen(true);
      toast.error(t('agents.crm.testErrorGeneric'));
    } finally {
      setIsTesting(false);
    }
  };

  const getStatusInfo = () => {
    if (!crmConfig.enabled) {
      return {
        color: 'default' as const,
        icon: <InfoIcon />,
        text: t('agents.crm.disabled'),
        description: t('agents.crm.notConfigured')
      };
    }

    if (!crmConfig.url) {
      return {
        color: 'warning' as const,
        icon: <ErrorIcon />,
        text: t('agents.crm.incomplete'),
        description: t('agents.crm.urlNotConfigured')
      };
    }

    return {
      color: 'success' as const,
      icon: <CheckCircleIcon />,
      text: t('agents.crm.active'),
      description: t('agents.crm.connectedTo', { url: crmConfig.url })
    };
  };

  const statusInfo = getStatusInfo();

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkIcon color="primary" />
              <Typography variant="h6">{t('agents.crm.title')}</Typography>
            </Box>
            <Chip
              icon={statusInfo.icon}
              label={statusInfo.text}
              color={statusInfo.color}
              variant="outlined"
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {statusInfo.description}
          </Typography>

          {crmConfig.enabled && crmConfig.url && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>{t('agents.crm.url')}</strong> {crmConfig.url}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('agents.crm.method')}</strong> {crmConfig.method}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('agents.crm.mappedFields')}</strong> {crmConfig.fieldMappings?.length || 0}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('agents.crm.headers')}</strong> {crmConfig.headers?.length || 0}
                </Typography>
              </Alert>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={crmConfig.enabled}
                onChange={(e) => handleToggleCRM(e.target.checked)}
                color="primary"
              />
            }
            label={t('agents.crm.enable')}
          />
        </CardContent>

        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Button
            variant="outlined"
            startIcon={isSaving ? <CircularProgress size={16} /> : <SettingsIcon />}
            onClick={() => setConfigDialogOpen(true)}
            disabled={!crmConfig.enabled || isSaving}
          >
            {isSaving ? t('agents.crm.saving') : t('agents.crm.configure')}
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={isTesting ? <CircularProgress size={16} /> : <TestIcon />}
            onClick={handleTestCRM}
            sx={{
                color: theme.palette.mode === 'dark' ? '#000' : undefined,
            }}
            disabled={!crmConfig.enabled || !crmConfig.url || isTesting || isSaving}
          >
            {isTesting ? t('agents.crm.testing') : t('agents.crm.test')}
          </Button>
        </CardActions>
      </Card>

      <CRMConfigurationDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        onSave={handleConfigSave}
        initialConfig={crmConfig}
      />

      <Dialog
        open={testResultDialogOpen}
        onClose={() => setTestResultDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {testResult?.success ? (
            <CheckCircleIcon color="success" />
          ) : (
            <ErrorIcon color="error" />
          )}
          {t('agents.crm.testResult')}
        </DialogTitle>
        <DialogContent>
          <Alert 
            severity={testResult?.success ? 'success' : 'error'} 
            sx={{ mb: 2 }}
          >
            {testResult?.message}
          </Alert>
          
          {testResult?.testData && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t('agents.crm.testData')}
              </Typography>
              <Box sx={{ 
                bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100', 
                p: 2, 
                borderRadius: 1,
                border: 1,
                borderColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300'
              }}>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  <strong>{t('agents.crm.leadId')}</strong> {testResult.testData.leadId}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  <strong>{t('agents.crm.leadName')}</strong> {testResult.testData.leadName}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  <strong>{t('agents.crm.leadPhone')}</strong> {testResult.testData.leadPhone}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  <strong>{t('agents.crm.crmUrl')}</strong> {testResult.testData.crmUrl}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  <strong>{t('agents.crm.methodLabel')}</strong> {testResult.testData.method}
                </Typography>
              </Box>
            </Box>
          )}
          
          {testResult?.error && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, color: 'error.main' }}>
                {t('agents.crm.errorDetails')}
              </Typography>
              <Box sx={{ 
                bgcolor: theme.palette.mode === 'dark' ? 'error.dark' : 'error.light', 
                p: 2, 
                borderRadius: 1,
                border: 1,
                borderColor: 'error.main'
              }}>
                <Typography variant="body2" sx={{ 
                  color: theme.palette.mode === 'dark' ? 'error.light' : 'error.contrastText'
                }}>
                  {testResult.error}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestResultDialogOpen(false)}>
            {t('agents.crm.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CRMIntegrationCard;
